import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams, Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {usePreventFileDropNavigation} from '../../../hooks/usePreventFileDropNavigation';
import {
    DOC_SOURCE, MAX_TRANSCRIPT_CHARS, URL_ERROR_KEYS, uploadErrorKeyFor, uploadErrorParamsFor,
} from './docConstants';
import {isDeclarableKind, kindLabelKey, kindDescKey, kindIcon} from './contextPackConstants';
import {
    FILE_KIND, FORMATS_KEY, INGEST_ACCEPT, INGEST_MODE, classifyFile, formatBytes,
    readAudioDuration,
} from './ingestFiles';

// Ícono FontAwesome 5 (webfont `<i class="fas">`) por tipo de archivo. Nombres FA5: un nombre FA6+
// renderiza vacío sin error.
const SETUP_FILE_ICON = {
    [FILE_KIND.PDF]: 'file-pdf',
    [FILE_KIND.DOCX]: 'file-word',
    [FILE_KIND.IMAGE]: 'file-image',
    [FILE_KIND.AUDIO]: 'file-audio',
};

// Vista "Cargar": sube material del cliente a la Memoria. Dos modos, según venga `?kind=` en la URL:
//
//  - SIN kind (entrada por el menú "+ Agregar" o el ítem de reunión del checklist): el flujo de
//    siempre — una transcripción de reunión (`source=MEETING`), cuyo tipo lo fija esa fuente.
//  - CON kind (entrada desde un ítem del Paquete de Contexto, ej. "Brief de alta"): el material
//    entra como carga manual con el tipo YA DECLARADO por quien lo sube. Esa declaración le gana al
//    clasificador y sobrevive a las re-extracciones, así que el ítem del checklist queda marcado.
//
// El archivo elegido puede ser texto (se lee acá y viaja como `raw_text`, como siempre) o un
// binario (PDF / Word / imagen / audio): ahí NO se llena el textarea — se muestra un chip del
// archivo y el envío va por el endpoint multipart, con progreso en el botón. Qué entra lo decide
// `ingestFiles.classifyFile`, la misma tabla que la Barra Total del Recorrido. Un archivo NUNCA
// declara tipo por su cuenta: sólo viaja el que la agencia eligió, porque un `document_kind`
// declarado apaga al clasificador del backend para siempre (ver `submitUpload`).
//
// El cliente queda fijado por la ruta = match humano (no hay auto-match). "Procesar" crea el
// ClientDocument PENDING y dispara la extracción async; el resultado aparece en Documentos/Hechos.
//
// Las salidas de esta pantalla apuntan a `sources` y `resumen`, que desde 2026-08-03 son las únicas
// que existen: la navegación de la Memoria dejó de depender del rediseño (antes había que elegir
// entre `sources` y la vieja `connections` según la generación, y el link a la que no correspondía
// caía en la primera pill de la lista sin abrir nada).
export function MemorySetupView({clientId}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [title, setTitle] = useState('');
    const [occurredOn, setOccurredOn] = useState('');
    const [buId, setBuId] = useState('');
    const [rawText, setRawText] = useState('');
    // El archivo binario elegido (`{file, kind}`) — reemplaza al textarea mientras exista.
    const [attached, setAttached] = useState(null);
    const [progress, setProgress] = useState(null);
    const [businessUnits, setBusinessUnits] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    // Cancela la subida en curso: es la única salida cuando el socket se cuelga (el XHR tiene su
    // propio timeout, pero son cinco minutos y el usuario no tiene por qué esperarlos).
    const abortRef = useRef(null);
    usePreventFileDropNavigation();

    // Un `kind` desconocido en la URL se ignora en silencio y la pantalla cae al flujo de reunión:
    // un link viejo o mal tipeado no debe dejar al usuario sin poder cargar nada.
    const kindParam = searchParams.get('kind');
    const declaredKind = kindParam && isDeclarableKind(kindParam) ? kindParam : null;
    // Modo "Página web" (entrada propia del menú + Agregar): mismo formulario, copy de enlace.
    // La detección del enlace es del backend — este modo solo comunica la capacidad; pegar la
    // URL en cualquiera de los otros flujos funciona igual.
    const urlMode = !declaredKind && searchParams.get('mode') === 'url';

    const drivePath = `/client/${clientId}/memory/sources?connect=drive`;
    const formats = t(FORMATS_KEY);
    const escape = {interpolation: {escapeValue: false}};

    useEffect(() => {
        new APIClient(API_URL + 'client/' + clientId).call('GET').then((data) => {
            if (data && data.status === 'OK' && data.data) {
                setBusinessUnits((data.data.business_units || []).map((bu) => ({id: bu.id, name: bu.name})));
            }
        });
    }, [clientId]);

    const onFile = (e) => {
        const file = e.target.files && e.target.files[0];
        // Sin esto, elegir DOS VECES el mismo archivo no dispara `change`.
        e.target.value = '';
        if (!file) return;
        const verdict = classifyFile(file);
        if (verdict.mode === INGEST_MODE.REJECTED) {
            toast.error(t(verdict.reasonKey, {...verdict.reasonParams, formats, ...escape}));
            return;
        }
        if (verdict.mode === INGEST_MODE.UPLOAD) {
            setAttached({file, kind: verdict.kind});
            setRawText('');
            if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setRawText(String(reader.result || '').slice(0, MAX_TRANSCRIPT_CHARS));
            if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''));
        };
        reader.onerror = () => toast.error(t('client_memory.setup.file_error'));
        reader.readAsText(file);
    };

    const defaultTitle = declaredKind
        ? t(kindLabelKey(declaredKind))
        : t('client_memory.setup.default_title');

    const finishError = (data) => {
        setSubmitting(false);
        setProgress(null);
        if (data && data.status === 'ABORTED') return;   // lo canceló el usuario: sin toast
        const reasonKey = uploadErrorKeyFor(data)
            || (data && URL_ERROR_KEYS[data.reason_code]);
        // Mismo helper que la Barra Total: cuando cada superficie armaba sus propios valores, el
        // `{{max}}` de esta pantalla salía vacío («el máximo es .»).
        const params = uploadErrorParamsFor(data, {
            name: attached ? attached.file.name : '', formats,
        });
        toast.error(t(reasonKey || 'client_memory.toast.error', {...params, ...escape}));
    };

    const submitUpload = () => {
        setSubmitting(true);
        setProgress(0);
        // Sin esto, un socket colgado dejaba la pantalla en «Subiendo…» sin salida: el botón
        // «Quitar» está deshabilitado mientras sube y el único camino era recargar.
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = controller;
        const durationPromise = attached.kind === FILE_KIND.AUDIO
            ? readAudioDuration(attached.file)
            : Promise.resolve(null);
        durationPromise
            .then(durationSeconds => ClientMemoryApi.uploadDocumentFile({
                file: attached.file,
                agencyClientId: clientId,
                title: title.trim() || defaultTitle,
                // SÓLO si la agencia lo declaró. Un `document_kind` explícito se persiste con
                // origen USER y el guard `has_user_asserted_kind()` del backend hace que el
                // clasificador NUNCA lo toque — ni en la primera extracción ni en un retry. Poner
                // `MEETING_TRANSCRIPT` por default "para igualar al texto" rotulaba como
                // transcripción de reunión a todo brief en PDF que entrara por acá, y el checklist
                // del Paquete de Contexto tachaba el ítem equivocado para siempre. La paridad real
                // la da el backend: para un archivo de AUDIO fuerza `MEETING_TRANSCRIPT` por su
                // cuenta (`is_audio_material()`), sin declaración y sin bloquear al clasificador.
                documentKind: declaredKind || undefined,
                occurredOn: occurredOn || undefined,
                businessUnitId: buId || undefined,
                durationSeconds: durationSeconds || undefined,
                onProgress: setProgress,
                signal: controller ? controller.signal : undefined,
            }))
            .then((data) => {
                if (data && data.status === 'OK') {
                    setSubmitting(false);
                    setProgress(null);
                    toast.success(t('client_memory.setup.success'));
                    navigate(`/client/${clientId}/memory/resumen`);
                    return;
                }
                finishError(data);
            })
            .catch(() => finishError(null));
    };

    const submit = () => {
        if (attached) {
            submitUpload();
            return;
        }
        const text = rawText.trim();
        if (!text) {
            toast.error(t('client_memory.setup.empty_error'));
            return;
        }
        setSubmitting(true);
        const payload = {
            agency_client_id: clientId,
            raw_text: text,
            source: (declaredKind || urlMode) ? DOC_SOURCE.MANUAL : DOC_SOURCE.MEETING,
        };
        // En modo enlace, un título vacío NO manda default: el backend usa el <title> real de
        // la página, que siempre es mejor que "Documento sin título".
        if (title.trim() || !urlMode) {
            payload.title = title.trim() || defaultTitle;
        }
        if (declaredKind) payload.document_kind = declaredKind;
        if (occurredOn) payload.occurred_on = occurredOn;
        if (buId) payload.business_unit_id = buId;

        ClientMemoryApi.createDocument(payload)
            .then((data) => {
                setSubmitting(false);
                if (data && data.status === 'OK') {
                    toast.success(t('client_memory.setup.success'));
                    navigate(`/client/${clientId}/memory/resumen`);
                } else {
                    const reasonKey = data && URL_ERROR_KEYS[data.reason_code];
                    toast.error(t(reasonKey || 'client_memory.toast.error'));
                }
            })
            .catch(() => {
                setSubmitting(false);
                toast.error(t('client_memory.toast.error'));
            });
    };

    const overLimit = rawText.length > MAX_TRANSCRIPT_CHARS;
    const canSubmit = attached ? !submitting : (!submitting && Boolean(rawText.trim()) && !overLimit);

    let submitLabel = t('client_memory.setup.submit');
    if (submitting) {
        submitLabel = attached && progress !== null && progress < 1
            ? t('client_memory.setup.uploading', {percent: Math.round(progress * 100)})
            : t('client_memory.setup.processing');
    }

    const renderAttached = () => (
        <div className="cmem-setup-file" aria-label={t('client_memory.setup.file_label')}>
            <span className="cmem-setup-file-icon" aria-hidden="true">
                <i className={`fas fa-${SETUP_FILE_ICON[attached.kind] || 'file-alt'}`}/>
            </span>
            <span className="cmem-setup-file-name">{attached.file.name}</span>
            <span className="cmem-setup-file-size">{formatBytes(attached.file.size)}</span>
            {/* Durante la subida sigue vivo, y ahí CANCELA: deshabilitarlo dejaba la pantalla sin
                salida si el envío se colgaba. */}
            <button type="button" className="cmem-setup-file-remove"
                    onClick={() => {
                        if (abortRef.current) abortRef.current.abort();
                        setAttached(null);
                        setProgress(null);
                        setSubmitting(false);
                    }}>
                {submitting ? t('client_memory.setup.upload_cancel') : t('client_memory.setup.file_remove')}
            </button>
            {submitting && progress !== null && (
                <span className="cmem-setup-progress" role="progressbar"
                      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
                    <i style={{width: `${Math.round(progress * 100)}%`}}/>
                </span>
            )}
        </div>
    );

    return (
        <div className="cmem-setup">
            {declaredKind ? (
                <div className="cmem-setup-kind">
                    <span className="cmem-setup-kind-icon"><i className={`fas fa-${kindIcon(declaredKind)}`}/></span>
                    <span className="cmem-setup-kind-text">
                        <strong>{t(kindLabelKey(declaredKind))}</strong>
                        <span>{t(kindDescKey(declaredKind))}</span>
                    </span>
                </div>
            ) : (
                <p className="cmem-setup-intro">
                    {urlMode
                        ? t('client_memory.setup.url_intro')
                        : t('client_memory.setup.intro', {formats, ...escape})}
                </p>
            )}

            <div className="form-row">
                <label>{t('client_memory.setup.title_label')}</label>
                <input type="text" className="form-control" value={title}
                       placeholder={urlMode
                           ? t('client_memory.setup.url_title_placeholder')
                           : declaredKind
                               ? t('client_memory.setup.title_placeholder_doc')
                               : t('client_memory.setup.title_placeholder')}
                       onChange={(e) => setTitle(e.target.value)}/>
            </div>

            <div className="cmem-setup-grid">
                <div className="form-row">
                    <label>{(declaredKind || urlMode)
                        ? t('client_memory.setup.date_label_doc')
                        : t('client_memory.setup.date_label')}</label>
                    <input type="date" className="form-control" value={occurredOn}
                           onChange={(e) => setOccurredOn(e.target.value)}/>
                </div>
                <div className="form-row">
                    <label>{t('client_memory.setup.bu_label')}</label>
                    <select className="form-control" value={buId} onChange={(e) => setBuId(e.target.value)}>
                        <option value="">{t('client_memory.setup.bu_none')}</option>
                        {businessUnits.map((bu) => (
                            <option key={bu.id} value={bu.id}>{bu.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="form-row">
                <label>{attached
                    ? t('client_memory.setup.file_label')
                    : urlMode
                        ? t('client_memory.setup.url_label')
                        : declaredKind
                            ? t('client_memory.setup.content_label')
                            : t('client_memory.setup.transcript_label')}</label>
                {attached ? renderAttached() : (
                    <textarea className="form-control cmem-setup-textarea" rows={urlMode ? 3 : 12}
                              value={rawText}
                              maxLength={MAX_TRANSCRIPT_CHARS}
                              placeholder={urlMode
                                  ? t('client_memory.setup.url_placeholder')
                                  : declaredKind
                                      ? t('client_memory.setup.content_placeholder')
                                      : t('client_memory.setup.transcript_placeholder')}
                              onChange={(e) => setRawText(e.target.value)}/>
                )}
                <div className="cmem-setup-meta">
                    {/* Elegir un archivo no tiene sentido cuando lo que se carga es un enlace. */}
                    {!urlMode && (
                        <label className="cmem-file-label" htmlFor="cmem-file-upload">
                            {t(attached
                                ? 'client_memory.setup.change_file'
                                : 'client_memory.setup.upload_file', {formats, ...escape})}
                            <input id="cmem-file-upload" type="file"
                                   accept={INGEST_ACCEPT}
                                   onChange={onFile} className="cmem-file-input"/>
                        </label>
                    )}
                    {!attached && (
                        <span className={`cmem-char-count ${overLimit ? '-over' : ''}`}>
                            {`${rawText.length}/${MAX_TRANSCRIPT_CHARS}`}
                        </span>
                    )}
                </div>
            </div>

            <div className="cmem-setup-actions">
                <button type="button" className="btn -alt"
                        disabled={!canSubmit}
                        onClick={submit}>
                    {submitLabel}
                </button>
                {/* Los archivos que ya viven en Drive no hace falta copiarlos y pegarlos acá. */}
                <Link className="cmem-setup-alt-link" to={drivePath}>
                    {t('client_memory.setup.or_from_drive')}
                </Link>
            </div>
        </div>
    );
}
